import type {
  WidgetCoreDefinition,
  TeXMLContext,
  TeXMLElement,
  ValidationResult,
  TelnyxEvent,
  CallbackResult,
} from './types';
import { buildCallbackUrl } from './types';
import { getVoiceAttributes } from './voice';
import { interpolateVariables, resolveMessageWithRephrases } from './interpolate';
import type { Rephrases } from './interpolate';

export type GetCustomerIdConfig = {
  prompt: string | Record<string, string>;
  retryPrompt: string | Record<string, string>;
  retryCount: number;
  timeoutSeconds?: number;
  promptRephrases?: Rephrases;
  retryPromptRephrases?: Rephrases;
};

const DEFAULT_TIMEOUT_SECONDS = 10;

export function getCustomerIdRetryKey(nodeId: string): string {
  return `_getCustomerIdRetries_${nodeId}`;
}

function generateTeXML(config: GetCustomerIdConfig, context: TeXMLContext): TeXMLElement[] {
  const callbackUrl = buildCallbackUrl(context);
  const retryKey = getCustomerIdRetryKey(context.nodeId);
  const retryCount = parseInt(context.variables[retryKey] || '0', 10);
  const languageCode = context.variables._language;
  const defaultLang = context.languageConfig?.defaultLanguage;
  const rawTemplate = retryCount > 0 && config.retryPrompt ? config.retryPrompt : config.prompt;
  const visitKey = `_rephrase_${context.nodeId}`;
  const visitCount = parseInt(context.variables[visitKey] || '0', 10);
  const rephrases = retryCount > 0 && config.retryPrompt ? config.retryPromptRephrases : config.promptRephrases;
  const resolved = resolveMessageWithRephrases(rawTemplate, rephrases, visitCount, languageCode, defaultLang);
  const prompt = interpolateVariables(resolved, context.variables, context.speechFormats);
  context.variables[visitKey] = String(visitCount + 1);

  return [
    {
      tag: 'Gather',
      attributes: {
        input: 'dtmf',
        timeout: (config.timeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS).toString(),
        finishOnKey: '#',
        action: callbackUrl,
        method: 'POST',
      },
      children: [
        {
          tag: 'Say',
          attributes: getVoiceAttributes(
            context.voiceConfig,
            languageCode,
            context.languageConfig,
            context.elevenLabsApiKeyRef,
          ),
          content: prompt,
        },
      ],
    },
    {
      tag: 'Redirect',
      attributes: { method: 'POST' },
      content: callbackUrl,
    },
  ];
}

export function buildGetCustomerIdRetryResult(
  config: GetCustomerIdConfig,
  context: TeXMLContext,
  newRetryCount: number,
): CallbackResult {
  const callbackUrl = buildCallbackUrl(context);
  const languageCode = context.variables._language;
  const defaultLang = context.languageConfig?.defaultLanguage;
  const rawTemplate = config.retryPrompt || config.prompt;
  const retryVisitKey = `_rephrase_${context.nodeId}_retry`;
  const retryVisitCount = parseInt(context.variables[retryVisitKey] || '0', 10);
  const rephrases = config.retryPrompt ? config.retryPromptRephrases : config.promptRephrases;
  const resolved = resolveMessageWithRephrases(rawTemplate, rephrases, retryVisitCount, languageCode, defaultLang);
  const prompt = interpolateVariables(resolved, context.variables, context.speechFormats);

  const retryKey = getCustomerIdRetryKey(context.nodeId);

  return {
    variables: { [retryKey]: String(newRetryCount), [retryVisitKey]: String(retryVisitCount + 1) },
    elements: [
      {
        tag: 'Gather',
        attributes: {
          input: 'dtmf',
          timeout: (config.timeoutSeconds ?? DEFAULT_TIMEOUT_SECONDS).toString(),
          finishOnKey: '#',
          action: callbackUrl,
          method: 'POST',
        },
        children: [
          {
            tag: 'Say',
            attributes: getVoiceAttributes(
              context.voiceConfig,
              languageCode,
              context.languageConfig,
              context.elevenLabsApiKeyRef,
            ),
            content: prompt,
          },
        ],
      },
      {
        tag: 'Redirect',
        attributes: { method: 'POST' },
        content: callbackUrl,
      },
    ],
  };
}

export function getCustomerIdCallbackHandler(
  event: TelnyxEvent,
  config: GetCustomerIdConfig,
  variables: Record<string, string>,
  context: TeXMLContext,
): CallbackResult | 'needs_crm_lookup' {
  const digits = event.Digits;

  if (digits === '0') {
    return { nextHandleId: undefined };
  }

  if (!digits) {
    const retryKey = getCustomerIdRetryKey(context.nodeId);
    const retryCount = parseInt(variables[retryKey] || '0', 10);

    if (retryCount >= config.retryCount) {
      return { nextHandleId: 'noMatch', variables: { [retryKey]: '0' } };
    }

    return buildGetCustomerIdRetryResult(config, context, retryCount + 1);
  }

  return 'needs_crm_lookup';
}

function hasContent(value: string | Record<string, string>): boolean {
  if (typeof value === 'string') return value.trim().length > 0;
  return Object.values(value).some((v) => v.trim().length > 0);
}

function validateConfig(config: GetCustomerIdConfig): ValidationResult {
  const errors: string[] = [];

  if (!hasContent(config.prompt)) {
    errors.push('Prompt is required');
  }

  if (config.retryCount > 0 && !hasContent(config.retryPrompt)) {
    errors.push('Retry prompt is required');
  }

  if (config.retryCount < 0 || config.retryCount > 5) {
    errors.push('Retry count must be between 0 and 5');
  }

  if (config.timeoutSeconds !== undefined && (config.timeoutSeconds < 1 || config.timeoutSeconds > 60)) {
    errors.push('Timeout must be between 1 and 60 seconds');
  }

  return { valid: errors.length === 0, errors };
}

export const getCustomerIdWidgetCore: WidgetCoreDefinition<GetCustomerIdConfig> = {
  id: 'getCustomerId',
  version: 1,
  name: 'Get Customer ID',
  description: 'Collect customer ID digits from caller via DTMF input',
  category: 'input',
  tier: 'premium',

  defaultConfig: {
    prompt: 'Please enter your customer ID followed by the pound key.',
    retryPrompt: 'We did not receive your input. Please enter your customer ID followed by the pound key.',
    retryCount: 2,
    timeoutSeconds: DEFAULT_TIMEOUT_SECONDS,
  },

  variables: {
    outputs: [
      {
        name: 'customerId',
        description: 'The customer ID entered by the caller',
        type: 'string',
        availableOnHandleTypes: ['success'],
      },
    ],
  },

  texml: {
    generate: generateTeXML,
    requiresCallback: true,
  },

  validation: {
    validateConfig,
  },
};
